import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CreditCard, Calendar, AlertTriangle, CheckCircle, Download, Zap, Sparkles, Crown, Gift, Loader } from 'lucide-react';
import InvoiceList from './InvoiceList';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubscriptionManagement = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [subscription, setSubscription] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    loadSubscriptionData();
    
    // Check for payment success/failure from URL params
    const sessionId = searchParams.get('session_id');
    const paymentStatus = searchParams.get('payment');
    
    if (sessionId && paymentStatus === 'success') {
      pollPaymentStatus(sessionId);
    } else if (paymentStatus === 'cancelled') {
      toast.error('Zahlung abgebrochen');
      // Clean URL
      navigate('/provider/dashboard', { replace: true });
    }
  }, []);

  const loadSubscriptionData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [subRes, transRes, creditsRes] = await Promise.all([
        axios.get(`${API}/subscription/current`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/subscription/transactions`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/subscription/credits`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null }))  // Don't fail if credits endpoint errors
      ]);

      if (subRes.data.has_subscription) {
        setSubscription(subRes.data);
      }
      setTransactions(transRes.data.transactions);
      if (creditsRes.data) {
        setCredits(creditsRes.data);
      }
    } catch (error) {
      console.error('Error loading subscription:', error);
      toast.error('Fehler beim Laden der Abo-Daten');
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      toast.error('Zahlungsstatus konnte nicht überprüft werden. Bitte lade die Seite neu.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/subscription/checkout-status/${sessionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.payment_status === 'paid') {
        toast.success('Zahlung erfolgreich! Dein Abo ist jetzt aktiv.');
        // Clean URL and reload data
        navigate('/provider/dashboard', { replace: true });
        loadSubscriptionData();
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Zahlungssitzung abgelaufen. Bitte versuche es erneut.');
        navigate('/provider/dashboard', { replace: true });
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      toast.error('Fehler beim Überprüfen des Zahlungsstatus');
    }
  };

  const handleCancelSubscription = async () => {
    const { subscription: sub, plan_details: plan } = subscription;
    const periodEnd = formatDate(sub.current_period_end);
    const isYearly = plan.interval === 'year';
    const isTrial = sub.status === 'trial';
    
    let confirmMessage = '';
    
    if (isTrial) {
      // Special message for trial period
      confirmMessage = `Möchtest du dein Abo wirklich kündigen?\n\nWenn du jetzt kündigst, endet deine Testphase am ${formatDate(sub.trial_end)} und es wird keine Zahlung fällig.`;
    } else if (isYearly) {
      // Yearly plan
      confirmMessage = `Möchtest du dein Jahresabo wirklich kündigen?\n\nDein Abo bleibt bis zum Ende der aktuellen Jahresperiode aktiv und wird danach nicht automatisch verlängert.\n\nEnde der aktuellen Periode: ${periodEnd}`;
    } else {
      // Monthly plan
      confirmMessage = `Möchtest du dein Abo wirklich kündigen?\n\nDein Abo bleibt bis zum Ende der aktuellen Monatsperiode aktiv und wird danach nicht automatisch verlängert.\n\nEnde der aktuellen Periode: ${periodEnd}`;
    }
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setCancelling(true);
    try {
      const token = localStorage.getItem('token');
      const cancelResponse = await axios.post(
        `${API}/subscription/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (isTrial) {
        toast.success(`Abo gekündigt. Testphase läuft bis ${formatDate(sub.trial_end)}.`);
      } else {
        if (!cancelResponse.data?.period_end) {
          throw new Error('Stripe hat kein Ende der aktuellen Abrechnungsperiode zurückgegeben.');
        }
        toast.success(`Abo gekündigt. Läuft noch bis ${formatDate(cancelResponse.data.period_end)}.`);
      }
      loadSubscriptionData();
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      const errorMsg = error.response?.data?.detail || 'Fehler beim Kündigen des Abos';
      toast.error(errorMsg);
    } finally {
      setCancelling(false);
    }
  };

  const getPlanIcon = (planId) => {
    const icons = {
      basic_monthly: Zap,
      basic_yearly: Zap,
      plus: Sparkles,
      pro: Crown
    };
    const Icon = icons[planId] || Zap;
    return <Icon className="w-6 h-6" />;
  };

  const getStatusBadge = (status) => {
    const badges = {
      trial: { text: 'Testphase', color: 'bg-[#FFF0D1] text-[#2B2142]', icon: CheckCircle },
      active: { text: 'Aktiv', color: 'bg-green-100 text-green-800', icon: CheckCircle },
      cancelled: { text: 'Gekündigt', color: 'bg-red-100 text-red-800', icon: AlertTriangle },
      expired: { text: 'Abgelaufen', color: 'bg-gray-100 text-gray-800', icon: AlertTriangle }
    };
    const badge = badges[status] || badges.trial;
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold ${badge.color}`}>
        <Icon className="w-4 h-4" />
        {badge.text}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="w-8 h-8 animate-spin text-[#2B2142]" />
      </div>
    );
  }

  if (!subscription || !subscription.has_subscription) {
    return (
      <div className="bg-white rounded-3xl shadow-xl p-8">
        <div className="text-center py-12">
          <div className="w-20 h-20 bg-gradient-to-br from-[#2B2142] to-[#3D3158] rounded-full flex items-center justify-center mx-auto mb-6">
            <CreditCard className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Kein aktives Abo</h2>
          <p className="text-gray-600 mb-8">
            Dein Profil ist aktuell nicht sichtbar. Wähle einen Plan, um sichtbar zu werden und Anfragen zu erhalten.
          </p>
          <button
            onClick={() => navigate('/pricing')}
            className="px-8 py-4 bg-[#2B2142] hover:bg-[#1E1730] text-white font-semibold rounded-xl transition-colors"
          >
            Plan wählen
          </button>
        </div>
      </div>
    );
  }

  const { subscription: sub, plan_details: plan } = subscription;

  return (
    <div className="space-y-8">
      {/* Current Subscription */}
      <div className="bg-white rounded-3xl shadow-xl p-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Aktuelles Abo</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Plan Info */}
          <div className="border-2 border-gray-200 rounded-2xl p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-[#2B2142] to-[#3D3158] rounded-xl flex items-center justify-center">
                {getPlanIcon(sub.plan_id)}
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
                <p className="text-sm text-gray-600">
                  {plan.price.toFixed(2)} € / {plan.interval === 'month' ? 'Monat' : 'Jahr'}
                </p>
              </div>
            </div>
            
            <div className="mb-4">
              {getStatusBadge(sub.status)}
            </div>

            {sub.cancel_at_period_end && (
              <div className="mt-4 p-4 bg-yellow-50 border-2 border-yellow-200 rounded-xl">
                <p className="text-sm font-semibold text-yellow-800">
                  ⚠️ Abo zur Kündigung vorgemerkt
                </p>
                <p className="text-sm text-yellow-700 mt-1">
                  Läuft bis {formatDate(sub.current_period_end)} weiter
                </p>
                <p className="text-xs text-yellow-600 mt-1">
                  Keine automatische Verlängerung
                </p>
              </div>
            )}
            
            {!sub.cancel_at_period_end && (
              <div className="mt-4 p-4 bg-[#FFF0D1]/30 border border-[#2B2142]/20 rounded-xl">
                <p className="text-xs text-[#2B2142]">
                  {plan.interval === 'year' 
                    ? 'Dein Abo hat eine Laufzeit von 12 Monaten und kann zum Ende der aktuellen Jahresperiode gekündigt werden.' 
                    : 'Dein Abo wird monatlich abgerechnet und kann zum Ende jeder Monatsperiode gekündigt werden.'}
                </p>
              </div>
            )}
          </div>

          {/* Billing Info */}
          <div className="border-2 border-gray-200 rounded-2xl p-6">
            <h4 className="font-semibold text-gray-900 mb-4">Abrechnungsdetails</h4>
            
            <div className="space-y-3 text-sm">
              {sub.status === 'trial' && sub.trial_end && (
                <div className="flex items-center gap-2 text-[#2B2142]">
                  <Calendar className="w-4 h-4" />
                  <span className="font-semibold">Testphase bis: {formatDate(sub.trial_end)}</span>
                </div>
              )}
              
              <div className="flex justify-between">
                <span className="text-gray-600">Aktuelle Periode:</span>
                <span className="font-semibold text-gray-900">
                  {sub.current_period_start && sub.current_period_end ? (
                    `${formatDate(sub.current_period_start)} – ${formatDate(sub.current_period_end)}`
                  ) : 'N/A'}
                </span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-gray-600">Nächste Abrechnung:</span>
                <span className="font-semibold text-gray-900">
                  {sub.cancel_at_period_end ? (
                    <span className="text-yellow-600">Wird nicht verlängert</span>
                  ) : (
                    formatDate(sub.next_billing_date || sub.current_period_end)
                  )}
                </span>
              </div>
              
              {sub.payment_method && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Zahlungsart:</span>
                  <span className="font-semibold text-gray-900 capitalize">
                    {sub.payment_method === 'sepa' ? 'SEPA-Lastschrift' : 
                     sub.payment_method === 'card' ? 'Kreditkarte' : 
                     sub.payment_method === 'paypal' ? 'PayPal' : 'N/A'}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-4 mt-8">
          <button
            onClick={() => navigate('/pricing')}
            className="px-6 py-3 border-2 border-[#2B2142] text-[#2B2142] hover:bg-[#FFF0D1]/30 font-semibold rounded-xl transition-colors"
          >
            Plan wechseln
          </button>
          
          {!sub.cancel_at_period_end && (
            <button
              onClick={handleCancelSubscription}
              disabled={cancelling}
              className="px-6 py-3 border-2 border-red-600 text-red-600 hover:bg-red-50 font-semibold rounded-xl transition-colors disabled:opacity-50"
            >
              {cancelling ? 'Wird gekündigt...' : 'Abo kündigen'}
            </button>
          )}
        </div>
      </div>

      {/* Credits Section */}
      {credits && credits.credits && credits.credits.total_cents > 0 && (
        <div className="bg-white rounded-3xl shadow-xl p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
              <Gift className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Dein Guthaben</h2>
              <p className="text-sm text-gray-600">aus Empfehlungsprämien</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Total Credits */}
            <div className="p-6 bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl">
              <p className="text-sm text-green-700 mb-1">Verfügbares Guthaben</p>
              <p className="text-4xl font-bold text-green-600">
                {credits.credits.total_formatted}
              </p>
              <p className="text-sm text-green-600 mt-2">
                Wird automatisch auf deine nächste Rechnung angerechnet
              </p>
            </div>

            {/* Upcoming Invoice Preview */}
            {credits.upcoming_invoice && (
              <div className="p-6 border-2 border-gray-200 rounded-2xl">
                <p className="text-sm font-semibold text-gray-700 mb-4">Nächste Rechnung (Vorschau)</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Abo-Gebühr:</span>
                    <span className="font-medium">{(credits.upcoming_invoice.subtotal_cents / 100).toFixed(2).replace('.', ',')} €</span>
                  </div>
                  {credits.upcoming_invoice.credits_applied_cents < 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Guthaben:</span>
                      <span className="font-medium">{(credits.upcoming_invoice.credits_applied_cents / 100).toFixed(2).replace('.', ',')} €</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-lg border-t pt-2 mt-2">
                    <span>Zu zahlen:</span>
                    <span className={credits.upcoming_invoice.total_cents <= 0 ? 'text-green-600' : ''}>
                      {(credits.upcoming_invoice.total_cents / 100).toFixed(2).replace('.', ',')} €
                    </span>
                  </div>
                </div>
                {credits.upcoming_invoice.next_billing_date && (
                  <p className="text-xs text-gray-500 mt-3">
                    Fällig am {new Date(credits.upcoming_invoice.next_billing_date).toLocaleDateString('de-DE', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                )}
              </div>
            )}

            {/* No upcoming invoice - explain why */}
            {!credits.upcoming_invoice && sub.status === 'trial' && (
              <div className="p-6 border-2 border-[#2B2142]/20 bg-[#FFF0D1]/30 rounded-2xl">
                <p className="text-sm font-semibold text-[#2B2142] mb-2">Testphase aktiv</p>
                <p className="text-sm text-[#2B2142]">
                  Dein Guthaben wird auf deine erste Rechnung nach der Testphase angerechnet.
                </p>
              </div>
            )}
          </div>

          {/* Rewards List */}
          {credits.credits.rewards && credits.credits.rewards.length > 0 && (
            <div className="mt-6">
              <p className="text-sm font-semibold text-gray-700 mb-3">Deine Prämien</p>
              <div className="space-y-2">
                {credits.credits.rewards.map((reward, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <div>
                        <span className="font-medium text-gray-900">{reward.milestone_number}. Empfehlung erreicht</span>
                        {reward.applied_at && (
                          <span className="text-sm text-gray-500 ml-2">
                            · {new Date(reward.applied_at).toLocaleDateString('de-DE')}
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="font-bold text-green-600">
                      -{(reward.credit_amount_cents / 100).toFixed(2).replace('.', ',')} €
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Invoices */}
      <InvoiceList />
      
      {/* Old Transactions (hidden, kept for reference) */}
      {false && transactions.length > 0 && (
      <div className="bg-white rounded-3xl shadow-xl p-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Zahlungshistorie (Alt)</h2>
        {transactions.length === 0 ? (
          <p className="text-gray-600 text-center py-8">Noch keine Zahlungen vorhanden.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Datum</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Plan</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Betrag</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Aktion</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((transaction) => (
                  <tr key={transaction.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-4 px-4">
                      {formatDate(transaction.created_at)}
                    </td>
                    <td className="py-4 px-4">
                      {transaction.metadata?.plan_name || transaction.plan_id}
                    </td>
                    <td className="py-4 px-4 font-semibold">
                      {transaction.amount.toFixed(2)} € <span className="text-sm text-gray-500">(zzgl. MwSt.)</span>
                    </td>
                    <td className="py-4 px-4">
                      <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
                        Bezahlt
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <button className="text-[#2B2142] hover:underline font-semibold text-sm flex items-center gap-1">
                        <Download className="w-4 h-4" />
                        Rechnung
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        <p className="text-sm text-gray-500 mt-6">
          💡 Hinweis: Rechnungen können für Buchhaltungszwecke heruntergeladen werden.
        </p>
      </div>
      )}
    </div>
  );
};

export default SubscriptionManagement;
